import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import EmptyState from '../../../components/shared/EmptyState';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import en from '../../../translations/en.json';

function CasesNotesTab({ song, onSelectCase }) {
  const [casesList, setCasesList] = useState([]);
  const [notesList, setNotesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const fetchSongNotesAndCases = useCallback(async () => {
    if (!song || !song.id) return;
    try {
      setIsLoading(true);
      const queryParams = new URLSearchParams({
        songId: String(song.id),
        songName: song.name || '',
        status: 'all',
        limit: '100'
      });

      const res = await apiClient(`/notes-and-cases?${queryParams.toString()}`);
      if (res && res.items) {
        const cases = res.items.filter(item => item.type === 'case');
        const notes = res.items.filter(item => item.type === 'note');
        setCasesList(cases);
        setNotesList(notes);
      }
    } catch (err) {
      console.error('Error fetching song notes and cases:', err);
    } finally {
      setIsLoading(false);
    }
  }, [song]);

  useEffect(() => {
    fetchSongNotesAndCases();
  }, [song?.id]);

  return (
    <div className="cases-notes-tab-container" style={{ position: 'relative' }}>
      <LoadingOverlay isLoading={isLoading} message="Loading Notes & Cases..." />

      {/* Cases Section */}
      <div className="cases-section">
        {/* Section Header */}
        <div className="section-header-row">
          <div className="section-title-left">
            <div className="section-icon-circle circle-indigo">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5">
                <line x1="12" y1="22" x2="12" y2="2" />
                <path d="M5 22h14" />
                <path d="m19 16-5-3 5-3v6Z" />
                <path d="m5 16 5-3-5-3v6Z" />
              </svg>
            </div>
            <h3 className="section-title-label">{t('albumDetails.casesTitle') || 'Cases'}</h3>
            <span className="section-count-pill">{casesList.length} {t('albumDetails.casesCount') || 'Cases'}</span>
          </div>
        </div>

        {/* Grid of Case Cards */}
        {casesList.length === 0 ? (
          <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '30px', border: '1px solid #e2e8f0' }}>
            <EmptyState
              title="No Cases Logged"
              subtitle="No conflict or legal cases are currently registered for this song."
              hasSearch={false}
            />
          </div>
        ) : (
          <div className="cases-grid">
            {casesList.map((item) => {
              const isOpen = item.status === 1 || item.status === '1';

              return (
                <div key={item.id} className="case-card">
                  <div className="case-card-top">
                    <div className="case-icon-wrapper">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
                        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    </div>
                    <div className="case-info-details">
                      <h4 className="case-title">{item.name}</h4>
                      <span className="case-tags">Tags: {item.tags || 'Standard Case'}</span>
                    </div>
                  </div>

                  <div className="case-situation-box">
                    <span className="situation-title">
                      Situation: {item.currentSituation && item.currentSituation !== 'start' ? item.currentSituation : 'Initial Situation Logged'}
                    </span>
                    <div className={`situation-status-pill ${isOpen ? 'status-open' : 'status-closed'}`}>
                      <span className="status-dot"></span>
                      <span className="status-text">{isOpen ? 'Open' : 'Closed'}</span>
                    </div>
                  </div>

                  <div className="case-card-footer">
                    <button
                      type="button"
                      className="view-more-btn"
                      onClick={() => onSelectCase && onSelectCase(item)}
                    >
                      {t('albumDetails.casesViewMore') || 'View Case >'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <hr className="sections-divider" style={{ margin: '30px 0', borderColor: '#e2e8f0' }} />

      {/* Notes Section */}
      <div className="notes-section">
        <div className="section-header-row">
          <div className="section-title-left">
            <div className="section-icon-circle circle-blue">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <h3 className="section-title-label">{t('albumDetails.notesTitle') || 'Notes'}</h3>
            <span className="section-count-pill">{notesList.length > 0 ? notesList.length : (song?.notes ? 1 : 0)} {t('albumDetails.notesCount') || 'Notes'}</span>
          </div>
        </div>

        {notesList.length === 0 && !song?.notes ? (
          <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '30px', border: '1px solid #e2e8f0' }}>
            <EmptyState
              title="No Notes Logged"
              subtitle="No active notes recorded for this song."
              hasSearch={false}
            />
          </div>
        ) : (
          <div className="notes-grid">
            {notesList.length > 0 ? (
              notesList.map((note) => {
                const isOpen = note.status === 1 || note.status === '1';

                return (
                  <div key={note.id} className="note-card">
                    <div className="note-card-body">
                      <div className="note-icon-wrapper">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                      </div>
                      <div className="note-info-details" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '8px' }}>
                          <h4 className="note-title" style={{ margin: 0 }}>{note.name}</h4>
                          <div className={`situation-status-pill ${isOpen ? 'status-open' : 'status-closed'}`}>
                            <span className="status-dot"></span>
                            <span className="status-text">{isOpen ? 'Open' : 'Closed'}</span>
                          </div>
                        </div>
                        <span className="note-tags" style={{ display: 'block', marginTop: '4px' }}>{note.tags || note.description || 'Admin note'}</span>
                      </div>
                    </div>

                    <div className="note-card-footer">
                      <button
                        type="button"
                        className="view-more-btn"
                        onClick={() => onSelectCase && onSelectCase(note)}
                      >
                        {t('albumDetails.notesViewMore') || 'View Note >'}
                      </button>
                    </div>
                  </div>
                );
              })
            ) : song?.notes ? (
              <div key="song-admin-note" className="note-card">
                <div className="note-card-body">
                  <div className="note-icon-wrapper">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <div className="note-info-details" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '8px' }}>
                      <h4 className="note-title" style={{ margin: 0 }}>Admin Notes</h4>
                      <div className="situation-status-pill status-open">
                        <span className="status-dot"></span>
                        <span className="status-text">Open</span>
                      </div>
                    </div>
                    <span className="note-tags" style={{ display: 'block', marginTop: '4px' }}>{song.notes}</span>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default CasesNotesTab;
